// Commission rules sagas.
//
// Effect choice:
//   - list and detail use the takeLatest equivalent, so a filter change cannot
//     be overtaken by its own stale response
//   - validate is DEBOUNCED (600ms). It is a convenience alongside the explicit
//     "Test formulas" button, so the user is not firing a request per keystroke
//     while typing a formula. redux-saga 0.14 has no debounce effect; the
//     equivalent is built from race + take in makeWatcher.es6.js.
//   - create and update use the plain watcher; neither moves money

import { put, select } from 'redux-saga/effects';
import * as types from '../../lib/actionTypes.es6';
import * as actions from './actions.es6';
import {
  makeWatcher,
  makeLatestWatcher,
  makeDebouncedWatcher
} from '../../components/CommissionsShared/makeWatcher.es6';
import {
  getCommissionRules,
  getCommissionRule,
  createCommissionRule,
  updateCommissionRule,
  validateCommissionRule
} from '../../lib/requests.es6';

export var VALIDATE_DEBOUNCE_MS = 600;

export var getCommissionRulesRequestWatcher = makeLatestWatcher(
  types.GET_COMMISSION_RULES_REQUEST,
  getCommissionRules,
  actions.getCommissionRulesSuccess,
  actions.getCommissionRulesFailed
);

export var getCommissionRuleRequestWatcher = makeLatestWatcher(
  types.GET_COMMISSION_RULE_REQUEST,
  getCommissionRule,
  actions.getCommissionRuleSuccess,
  actions.getCommissionRuleFailed,
  {
    args: function(payload) {
      return [payload && payload.guid ? payload.guid : payload];
    }
  }
);

// Refresh the list after a write, reusing the filters the user had applied.
// Every filter is optional, so an empty params object is a valid "show all"
// refresh rather than a request to suppress.
function* refreshRuleList() {
  var params = yield select(function(state) {
    var slice = state.commissionRules;
    return (slice && slice.listParams) || {};
  });
  yield put(actions.getCommissionRulesRequest(params));
}

export var createCommissionRuleRequestWatcher = makeWatcher(
  types.CREATE_COMMISSION_RULE_REQUEST,
  createCommissionRule,
  actions.createCommissionRuleSuccess,
  actions.createCommissionRuleFailed,
  { onSuccessEffects: refreshRuleList }
);

export var updateCommissionRuleRequestWatcher = makeWatcher(
  types.UPDATE_COMMISSION_RULE_REQUEST,
  updateCommissionRule,
  actions.updateCommissionRuleSuccess,
  actions.updateCommissionRuleFailed,
  {
    args: function(payload) { return [payload.guid, payload.body || payload]; },
    onSuccessEffects: refreshRuleList
  }
);

export var validateCommissionRuleRequestWatcher = makeDebouncedWatcher(
  types.VALIDATE_COMMISSION_RULE_REQUEST,
  VALIDATE_DEBOUNCE_MS,
  validateCommissionRule,
  actions.validateCommissionRuleSuccess,
  actions.validateCommissionRuleFailed
);
